import { useState, useCallback } from "react";
import { useSnapshot } from "valtio";
import type { PortfolioItemProps, PortfolioItem } from "./types";
import { rootStore } from "@/src/store";

/**
 * Portfolio UI 交互 Hook
 *
 * 封装了 UI 交互相关的逻辑：
 * - Drawer 状态管理
 * - 自动关闭逻辑
 */
export const usePortfolioUI = (
  mode?: "select" | "checkbox",
  onItemClick?: PortfolioItemProps["onClick"],
  isPc?: boolean,
  popoverRef?: React.RefObject<{ close: () => void } | null>,
) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 获取登录状态
  const { isLoggedIn } = useSnapshot(rootStore.login);

  // 处理 Drawer 打开
  const handleDrawerOpen = useCallback(() => {
    if (!isLoggedIn) {
      return;
    }
    setDrawerOpen(true);
  }, [isLoggedIn]);

  // 处理 Drawer 关闭
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // 包装 onItemClick，在 select 模式下点击后自动关闭
  const handleItemClick = useCallback(
    (item: PortfolioItem, checked?: boolean) => {
      onItemClick?.(item, checked);

      // 如果是 select 模式，点击后自动关闭
      if (mode === "select") {
        if (isPc) {
          // PC 端关闭 Popover
          popoverRef?.current?.close();
        } else {
          // 移动端关闭 Drawer
          setDrawerOpen(false);
        }
      }
    },
    [onItemClick, mode, isPc, popoverRef],
  );

  return {
    drawerOpen,
    handleDrawerOpen,
    handleDrawerClose,
    handleItemClick,
  };
};
